import { useMemo } from 'react';
import { TELEPORT_LS_KEY } from '../../../../../Dogfood/Dogfood';
import { SuburbType } from '../../../../../corporate/search/LocationBox/SuburbType';
import { DefaultTextSearchExperiment } from '../../../../../search/experiments/DefaultTextSearchExperiment';
import { useExperiment } from '../../../../ExperimentContext/useExperiment';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../constants';
import { useDebouncedValue } from '../../../../hooks/useDebounceValue';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useHannibalQuery } from '../../../../schema/hannibal/HannibalContext';
import { AllSuggestionsDocument } from '../../../../schema/hannibal/types.generated';
import { useFlags } from '../../../../services/flags';
import { EMPTY_LIST } from '../../../../util/EMPTY_LIST';
import { mapCombinedSuggestionsToSuggestionData } from '../FilterSuggestions/FilterSuggestions';
import { mapPracticeSuggestionsToSuggestionData } from '../PracticeSuggestions/PracticeSuggestions';
import { mapPractitionerSuggestionsToSuggestionData } from '../PractitionerSuggestions/PractitionerSuggestions';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import { TabProps } from '../TabProps';
import { removeDuplicateSuggestions } from '../TreatmentSuggestions/TreatmentSuggestions';
import { treatmentFiltersFromProfiles } from '../TreatmentSuggestions/filtersFromProfiles';
import { sortFiltersByRelevance } from '../TreatmentSuggestions/sortFiltersByRelevance';
import { suggestionsFromFilters } from '../TreatmentSuggestions/suggestionsFromFilters';
import { mapSuggestedProfilesToSuggestionTreatmentProfiles } from '../TreatmentSuggestions/useSuggestedProfiles';
import { useTextSuggestion } from '../useTextSuggestion';

export const AllSuggestions = ({ searchTerm, onChoose, trackingData }: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const [teleportLocation] = useLocalStorage<SuburbType | null>(TELEPORT_LS_KEY, null);

  const geoFilter = teleportLocation
    ? { latitude: teleportLocation.latitude, longitude: teleportLocation.longitude }
    : undefined;

  const { data } = useHannibalQuery(AllSuggestionsDocument, {
    variables: { query, location: geoFilter },
  });

  const practitionerProfiles = data?.searchPractitionersByName.practitioners ?? EMPTY_LIST;

  const practitionerSuggestions = useMemo(
    () => mapPractitionerSuggestionsToSuggestionData(practitionerProfiles).slice(0, 3),
    [practitionerProfiles],
  );

  const practiceProfiles = data?.searchText.profiles ?? EMPTY_LIST;

  const practiceSuggestions = useMemo(
    () => mapPracticeSuggestionsToSuggestionData(practiceProfiles).slice(0, 3),
    [practiceProfiles],
  );

  const topicProfiles = data?.searchText.profiles ?? EMPTY_LIST;

  const topicSuggestions = useMemo(() => {
    const profiles = mapSuggestedProfilesToSuggestionTreatmentProfiles(topicProfiles);
    const rawFilters = treatmentFiltersFromProfiles(profiles);

    const sortedFilters = sortFiltersByRelevance(rawFilters, searchTerm);

    const rawSuggestions = suggestionsFromFilters(sortedFilters);

    return removeDuplicateSuggestions(rawSuggestions).slice(0, 3);
  }, [topicProfiles, searchTerm]);

  const specialties = data?.searchSuggestions.suggestions ?? EMPTY_LIST;

  const specialtySuggestions = useMemo(
    () => mapCombinedSuggestionsToSuggestionData(specialties, false).slice(0, 3),
    [specialties],
  );

  const allSuggestions = [
    ...specialtySuggestions,
    ...practiceSuggestions,
    ...practitionerSuggestions,
    ...topicSuggestions,
  ];

  const defaultTextSearchExperiment = Boolean(useExperiment(DefaultTextSearchExperiment).data);

  const useDefaultTextSearch =
    (useFlags().defaultTextSearch ?? false) || defaultTextSearchExperiment;

  const allResults = useTextSuggestion(searchTerm, allSuggestions, useDefaultTextSearch);
  return (
    <SuggestionList
      suggestions={allResults}
      onChoose={onChoose}
      trackingData={trackingData}
      searchTerm={searchTerm}
    />
  );
};
